import { Icon as FunctionalIcon } from "../ui/Icon.js";
import { useEffect, useState } from "react";
import { pick, useT } from "../i18n/index.js";
import { Panel } from "../ui/Panel.js";
import { Button } from "../ui/Button.js";
import { useStore } from "../store/state.js";
import { BundleList } from "../store/cards.js";
import { useEvents } from "./state.js";
import { achievementsApi } from "./api.js";

const CATEGORIES = ["building", "units", "combat", "world", "store", "social"] as const;

/** 120 achievements with lifetime progress; claiming pays the chest and unlocks titles and frames (R64). */
export function AchievementsView() {
  const t = useT();
  const view = useEvents((s) => s.achievements);
  const refresh = useEvents((s) => s.refreshAchievements);
  const run = useEvents((s) => s.run);
  const celebrate = useStore((s) => s.celebrate);
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]>("building");
  useEffect(() => {
    void refresh();
  }, [refresh]);
  if (!view) return <p className="hint">{t("common.loading")}</p>;
  const claim = async (id: string) => {
    const res = await run(() => achievementsApi.claim(id), t("events.toast.achievement"));
    if (res) {
      celebrate({ key: id, granted: res.granted, doubled: false, vipPoints: 0, nextStep: null });
      void refresh();
    }
  };
  const list = view.list.filter((a) => a.category === category);
  // Show every step of a ladder but lead with the next open one.
  const claimableFirst = [...list].sort((a, b) => Number(b.claimable) - Number(a.claimable));
  return (
    <Panel title={t("events.tab.achievements")} actions={<span className="badge gold mono" data-testid="achievement-points">{view.points} P</span>}>
      <div className="row tabs">
        {CATEGORIES.map((c) => (
          <button key={c} type="button" className={`tab ${c === category ? "active" : ""}`} onClick={() => setCategory(c)} data-testid={`ach-cat-${c}`}>
            {t(`events.ach.${c}`)}
            {view.list.some((a) => a.category === c && a.claimable) ? <span className="badge gold" style={{ marginLeft: 6 }}>!</span> : null}
          </button>
        ))}
      </div>
      {view.titles.length ? <p className="hint">{t("events.ach.titles", { n: view.titles.length })}</p> : null}
      <div className="missions" data-testid="achievement-list">
        {claimableFirst.map((a) => {
          const pct = Math.min(100, Math.round((a.progress / a.target) * 100));
          return (
            <div key={a.id} className={`mission ${a.claimed ? "done" : ""}`} data-testid={`ach-${a.id}`}>
              <div className="row">
                <span className="grow ellipsis">{pick(a.name)}</span>
                <span className="mono dim" style={{ fontSize: 11 }}>
                  {Math.min(a.progress, a.target)}/{a.target}
                </span>
                {a.claimable ? (
                  <Button variant="gold" onClick={() => void claim(a.id)} data-testid={`ach-claim-${a.id}`}>
                    {t("common.claim")}
                  </Button>
                ) : a.claimed ? (
                  <span className="dim"><FunctionalIcon name="check" /></span>
                ) : (
                  <span className="badge mono">+{a.points}</span>
                )}
              </div>
              <div className="progress" style={{ marginTop: 4 }}>
                <div className={`progress-fill ${a.claimed ? "gold" : ""}`} style={{ width: `${pct}%` }} />
              </div>
              <div className="row" style={{ gap: 8 }}>
                <BundleList bundle={a.reward} compact />
                {a.title ? <span className="badge gold">{t("events.ach.title", { title: a.title })}</span> : null}
                {a.frame ? <span className="badge">{t("events.ach.frame", { frame: a.frame })}</span> : null}
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}
